/**
 * ASC 606 Phase 11g — Recognition Events audit browse.
 *
 * Reusable read-only panel that renders revenue_recognition_events for a
 * given POB (11c append-only audit log). Used by ProgressTracker tabs
 * (Cost-to-Cost, Milestones, Units) to show the same audit trail.
 *
 * Backend: GET /api/v1/revenue/contracts/{id}/pobs/{pob_id}/recognition-events
 */
import { useEffect, useState, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../../constants';
import DataTable, { Column } from './DataTable';

const API = `${API_V1}/revenue`;

interface RecognitionEvent {
  event_id: number;
  period: string;
  progress_method: string;
  status: string;
  baseline_amount: number | null;
  actual_numerator: number | null;
  excluded_numerator: number | null;
  pct_before: number | null;
  pct_after: number | null;
  amount_before: number | null;
  amount_after: number | null;
  period_delta: number | null;
  je_debit_side: string | null;
  cumulative_billings: number | null;
  je_id: number | null;
  je_number: string | null;
  actor_user_id: number | null;
  trigger_source: string;
  source_note: string | null;
  created_at: string | null;
}

interface Props {
  contractId: number;
  pobId: number;
  filterMethod?: string;
  title?: string;
}

export default function RecognitionEventsPanel({ contractId, pobId, filterMethod, title }: Props) {
  const [events, setEvents] = useState<RecognitionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(
        `${API}/contracts/${contractId}/pobs/${pobId}/recognition-events?limit=200`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [contractId, pobId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = filterMethod
    ? events.filter((e) => e.progress_method === filterMethod)
    : events;

  const statusBadge = (s: string) => {
    const bg =
      s === 'RECOGNIZED'
        ? 'rgba(34,197,94,0.15)'
        : s === 'FAILED'
        ? 'rgba(239,68,68,0.15)'
        : 'rgba(100,116,139,0.15)';
    const color =
      s === 'RECOGNIZED'
        ? 'var(--positive)'
        : s === 'FAILED'
        ? 'var(--negative)'
        : 'var(--text-secondary)';
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: 4,
          fontSize: 10,
          fontWeight: 600,
          background: bg,
          color,
        }}
      >
        {s}
      </span>
    );
  };

  const cols: Column[] = [
    { key: 'period', label: 'Period', width: 90 },
    {
      key: 'status',
      label: 'Status',
      render: (v: string) => statusBadge(v),
    },
    {
      key: 'progress_method',
      label: 'Method',
      render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span>,
    },
    {
      key: 'pct_after',
      label: 'Pct',
      align: 'right',
      render: (v: number | null) => (v == null ? '-' : `${(v * 100).toFixed(2)}%`),
    },
    {
      key: 'period_delta',
      label: 'Δ This Period',
      align: 'right',
      render: (v: number | null) => (v == null ? '-' : formatAmount(v)),
    },
    {
      key: 'amount_after',
      label: 'Cum Recognized',
      align: 'right',
      render: (v: number | null) => (v == null ? '-' : formatAmount(v)),
    },
    {
      key: 'je_debit_side',
      label: 'JE DR',
      render: (v: string | null) => v || '-',
    },
    {
      key: 'trigger_source',
      label: 'Trigger',
      render: (v: string) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v}</span>,
    },
    { key: 'je_number', label: 'JE #', render: (v: string | null) => v || '-' },
    {
      key: 'created_at',
      label: 'Posted',
      render: (v: string | null) =>
        v ? new Date(v).toLocaleString() : '-',
    },
  ];

  return (
    <div>
      {title && (
        <h3
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 10,
          }}
        >
          {title}
        </h3>
      )}
      {error && (
        <div
          style={{
            padding: 10,
            marginBottom: 10,
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid var(--negative)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--negative)',
          }}
        >
          {error}
        </div>
      )}
      <DataTable
        columns={cols}
        rows={filtered}
        loading={loading}
        emptyMessage="No recognition events yet for this POB."
        rowKey="event_id"
      />
    </div>
  );
}
